import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Users as UsersIcon, Clock, Loader2, ChevronRight, Vote, Heart, Eye } from 'lucide-react';
import { api } from '@/lib/api';

// V2 Weekly Platform Challenge card — sits on the Dashboard at first glance.
// Handles both 'metric' (auto-tracked) and 'submission_voting' (board + votes)
// admin challenge types. All users see the same active challenge.
export default function WeeklyChallengeCard({ onOpen }) {
  const [data, setData] = useState(null);
  const [joining, setJoining] = useState(false);
  const [peekIdx, setPeekIdx] = useState(0);
  const [topSubs, setTopSubs] = useState([]); // for submission_voting only

  const load = async () => {
    try {
      const { data: d } = await api.get('/challenges/current');
      setData(d);
      // Fetch top submissions for voting challenges so the card can rotate
      // a tiny "peek" of the best-voted entries — micro-FOMO that nudges joins.
      const c = d?.challenge;
      if (c && (c.challenge_type || 'metric') === 'submission_voting') {
        try {
          const { data: sd } = await api.get(`/challenges/${c.id}/submissions`);
          setTopSubs((sd.submissions || []).slice(0, 3));
        } catch { /* peek is optional */ }
      }
    } catch (e) {
      if (e) console.warn('challenge load failed', e?.message);
    }
  };

  useEffect(() => { load(); }, []);

  // Rotate the peek every 4s when we have 2+ top submissions.
  useEffect(() => {
    if (topSubs.length < 2) return undefined;
    const t = setInterval(() => setPeekIdx((i) => (i + 1) % topSubs.length), 4000);
    return () => clearInterval(t);
  }, [topSubs.length]);

  const join = async () => {
    setJoining(true);
    try {
      await api.post('/challenges/current/join');
      toast.success('You are in — let’s go!');
      await load();
      onOpen?.();
    } catch (e) {
      toast.error('Could not join');
    } finally {
      setJoining(false);
    }
  };

  if (!data?.challenge) return null;
  const c = data.challenge;
  const isVoting = (c.challenge_type || 'metric') === 'submission_voting';
  const peek = topSubs[peekIdx];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-3xl p-4 mb-2 shadow-lg text-white relative overflow-hidden z-10"
      style={{ background: `linear-gradient(135deg, ${c.color || '#7E5BEF'}E6 0%, #1F2937 120%)` }}
      data-testid="weekly-challenge-card"
    >
      {/* LIVE pulse — only while the challenge is actually running */}
      {(c.is_active || c.status === 'published') && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-full px-2 py-0.5" data-testid="weekly-challenge-live">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF4B4B] opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FF4B4B]" />
          </span>
          <span className="text-[9px] font-extrabold uppercase tracking-wider">Live</span>
        </div>
      )}

      <div className="flex items-start gap-3 pr-12">
        <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center text-2xl flex-shrink-0">
          <span aria-hidden>{c.emoji || (isVoting ? '🗳️' : '🏆')}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase font-extrabold opacity-80 tracking-wider">
            {isVoting ? 'This week’s contest' : 'This week’s challenge'}
          </p>
          <h3 className="text-lg font-extrabold leading-tight" data-testid="weekly-challenge-title">
            {c.title}
          </h3>
          <p className="text-xs font-bold opacity-90 mt-0.5 line-clamp-2">
            {isVoting ? (c.submission_prompt || c.tagline) : c.tagline}
          </p>
          {isVoting && (
            <p className="text-[10px] font-extrabold opacity-75 mt-1 flex items-center gap-1" data-testid="weekly-voting-rule">
              <Heart size={10} strokeWidth={3} fill="currentColor" /> Winners chosen by audience votes
            </p>
          )}
        </div>
        <span className="absolute top-9 right-3 text-[10px] font-extrabold bg-white/15 rounded-full px-2 py-1 flex items-center gap-1">
          <Trophy size={11} strokeWidth={3} /> +{c.reward_coins || 0}
        </span>
      </div>

      {/* Body — type-specific */}
      {isVoting ? (
        <>
          <div className="mt-3 flex items-center gap-3 text-[11px] font-extrabold opacity-95 flex-wrap">
            <span className="inline-flex items-center gap-1" data-testid="weekly-challenge-submissions">
              <Vote size={12} strokeWidth={3} /> {data.submissions || 0} entries
            </span>
            <span className="inline-flex items-center gap-1" data-testid="weekly-challenge-votes">
              <Heart size={12} strokeWidth={3} /> {data.votes || 0} votes
            </span>
            <span className="inline-flex items-center gap-1" data-testid="weekly-challenge-views">
              <Eye size={12} strokeWidth={3} /> {c.view_count || 0} views
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock size={12} strokeWidth={3} />
              {data.days_left > 0 ? `${data.days_left}d left` : `${data.hours_left || 0}h left`}
            </span>
          </div>

          {/* Rotating peek of the top-voted submission's first line */}
          {peek && (
            <div className="mt-3 bg-white/12 backdrop-blur-sm rounded-2xl px-3 py-2" data-testid="weekly-challenge-peek">
              <AnimatePresence mode="wait">
                <motion.div
                  key={peek.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="flex items-center gap-1.5 text-[10px] font-extrabold opacity-80 mb-0.5">
                    <Heart size={10} strokeWidth={3} fill="currentColor" /> {peek.votes || 0} · Top entry by {peek.author?.display_name || 'Member'}
                  </div>
                  <p className="text-xs font-bold leading-snug line-clamp-2 italic">
                    “{(peek.text || '').trim().slice(0, 120)}{(peek.text || '').length > 120 ? '…' : ''}”
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-2 text-[11px] font-extrabold">
            <span data-testid="weekly-challenge-progress">
              {data.progress || 0}/{data.target || 0}
            </span>
            <div className="flex-1 h-2 rounded-full bg-white/15 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-white"
                initial={{ width: 0 }}
                animate={{ width: `${data.percent || 0}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className="opacity-80">{data.percent || 0}%</span>
          </div>
          <div className="flex items-center gap-3 mt-3 text-[11px] font-extrabold opacity-90">
            <span className="flex items-center gap-1" data-testid="weekly-challenge-participants">
              <UsersIcon size={12} strokeWidth={3} /> {data.participants || 0} joined
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} strokeWidth={3} /> {data.days_left || 0}d left
            </span>
          </div>
        </>
      )}

      {/* CTA row */}
      <div className="flex items-center mt-3">
        {isVoting ? (
          <button
            onClick={() => onOpen?.()}
            className="ml-auto bg-white text-[#1F2937] rounded-full px-4 py-1.5 text-[11px] font-extrabold flex items-center gap-1"
            data-testid="weekly-challenge-view"
          >
            {data.has_submitted ? 'View board' : 'Open + submit'} <ChevronRight size={12} strokeWidth={3} />
          </button>
        ) : !data.joined ? (
          <button
            onClick={join}
            disabled={joining}
            className="ml-auto bg-white text-[#1F2937] rounded-full px-4 py-1.5 text-[11px] font-extrabold flex items-center gap-1"
            data-testid="weekly-challenge-join"
          >
            {joining ? <Loader2 size={12} className="animate-spin" /> : 'Join'} <ChevronRight size={12} strokeWidth={3} />
          </button>
        ) : data.completed ? (
          <span
            className="ml-auto bg-[#58CC02] text-white rounded-full px-4 py-1.5 text-[11px] font-extrabold"
            data-testid="weekly-challenge-completed"
          >
            Completed ✓
          </span>
        ) : (
          <button
            onClick={() => onOpen?.()}
            className="ml-auto bg-white/20 text-white rounded-full px-4 py-1.5 text-[11px] font-extrabold flex items-center gap-1"
            data-testid="weekly-challenge-view"
          >
            View board <ChevronRight size={12} strokeWidth={3} />
          </button>
        )}
      </div>
    </motion.div>
  );
}
